import {
  doc,
  getDocFromServer,
  onSnapshot,
  setDoc,
  type DocumentSnapshot,
  type Firestore,
} from 'firebase/firestore';
import type { Auth } from 'firebase/auth';
import { ulid } from 'ulid';
import {
  clientCommandSchema,
  getCommandDefinition,
  type ClientCommand,
  type CommandType,
  type PayloadOf,
  type StoredCommand,
} from '@ecobox/schema';
import { clampOffset } from './device';
import { createConnectivity, type Connectivity } from './connectivity';
import {
  createLedger,
  isFinalState,
  type Ledger,
  type LedgerEntry,
  type LedgerError,
  type LedgerPatch,
  type LedgerState,
} from './ledger';
import type { ReadableStore } from './store';

export type CommandErrorCode =
  'unknown_type' | 'invalid_payload' | 'invalid_envelope' | 'not_signed_in' | 'no_branch';

export class CommandValidationError extends Error {
  readonly code: CommandErrorCode;
  readonly issues: readonly string[];
  constructor(code: CommandErrorCode, message: string, issues: readonly string[] = []) {
    super(message);
    this.name = 'CommandValidationError';
    this.code = code;
    this.issues = issues;
  }
}

export interface CommandWriterOptions {
  db: Firestore;
  auth: Auth;
  deviceId: string;
  appVersion: string;
  /** The branch the person is working at; a 2–8 character code. */
  getBranchId: () => string | null | undefined;
  /** Device minus server in ms, as last measured. */
  clockOffset: number | (() => number);
  ledger?: Ledger;
  connectivity?: Connectivity;
  /** How long a command may stay unseen by the server, while online, before "not delivered". */
  undeliveredAfterMs?: number;
  sweepIntervalMs?: number;
  now?: () => number;
}

export interface SendResult {
  id: string;
  command: ClientCommand;
  /** Resolves when the command is in the device ledger. */
  accepted: Promise<void>;
  /** Resolves with the outcome of the write: the server acknowledged it, or the write failed. Never rejects. */
  ack: Promise<'acknowledged' | 'failed'>;
}

export interface SendOptions {
  /**
   * When the physical event happened, as epoch ms, if that is not "now". A batch of scans that is
   * sent later still carries the time of its first scan (02 §2.2, Ordering and conflicts).
   */
  occurredAtMs?: number;
}

export interface CommandWriter extends ReadableStore<readonly LedgerEntry[]> {
  readonly ledger: Ledger;
  send<T extends CommandType>(type: T, payload: PayloadOf<T>, options?: SendOptions): SendResult;
  send(type: string, payload: unknown, options?: SendOptions): SendResult;
  /** Re-write the same command (same id, so harmless if the server already has it). */
  resend(id: string): Promise<void>;
  /** Hide a rejected command the person has seen. */
  dismiss(id: string): Promise<void>;
  /** Mark long-unseen pending commands "not delivered". Returns how many changed. */
  markUndelivered(now?: number): number;
  /** Load the ledger, watch every command that is not final, and start the sweep. */
  start(): Promise<void>;
  dispose(): void;
}

const DEFAULT_UNDELIVERED_MS = 45_000;
const DEFAULT_SWEEP_MS = 5_000;

const FINAL_STATUS_STATE = {
  applied: 'applied',
  applied_with_exception: 'applied_with_exception',
  rejected: 'rejected',
} as const;

function errorCode(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return 'unknown';
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Validate the envelope and the type's payload. Throws before anything is written. */
export function buildCommand(input: {
  type: string;
  payload: unknown;
  actorUid: string | null | undefined;
  deviceId: string;
  branchId: string | null | undefined;
  appVersion: string;
  clockOffsetMs: number;
  nowMs: number;
}): ClientCommand {
  const definition = getCommandDefinition(input.type);
  if (!definition) {
    throw new CommandValidationError('unknown_type', `Unknown command type "${input.type}".`);
  }
  if (!input.actorUid) {
    throw new CommandValidationError('not_signed_in', 'Sign in before sending commands.');
  }
  if (!input.branchId) {
    throw new CommandValidationError('no_branch', 'Choose a branch before sending commands.');
  }
  const payload = definition.payload.safeParse(input.payload);
  if (!payload.success) {
    throw new CommandValidationError(
      'invalid_payload',
      `Invalid payload for ${input.type}.`,
      payload.error.issues.map((i) => `${i.path.join('.') || '(payload)'}: ${i.message}`),
    );
  }
  const envelope = clientCommandSchema.safeParse({
    id: `cmd_${ulid(input.nowMs)}`,
    type: input.type,
    actorUid: input.actorUid,
    deviceId: input.deviceId,
    branchId: input.branchId,
    occurredAt: new Date(input.nowMs).toISOString(),
    clockOffsetMs: clampOffset(input.clockOffsetMs),
    appVersion: input.appVersion,
    payload: payload.data,
  });
  if (!envelope.success) {
    throw new CommandValidationError(
      'invalid_envelope',
      'The command envelope is not valid.',
      envelope.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
    );
  }
  return envelope.data;
}

export function createCommandWriter(options: CommandWriterOptions): CommandWriter {
  const {
    db,
    auth,
    deviceId,
    appVersion,
    getBranchId,
    clockOffset,
    ledger = createLedger(),
    connectivity = createConnectivity(),
    undeliveredAfterMs = DEFAULT_UNDELIVERED_MS,
    sweepIntervalMs = DEFAULT_SWEEP_MS,
    now = Date.now,
  } = options;

  const watchers = new Map<string, () => void>();
  let sweepTimer: ReturnType<typeof setInterval> | undefined;

  const unwatch = (id: string): void => {
    watchers.get(id)?.();
    watchers.delete(id);
  };

  const apply = (id: string, state: LedgerState, extra: LedgerPatch = {}): void => {
    const entry = ledger.get(id);
    if (!entry || isFinalState(entry.state)) return;
    void ledger.patch(id, { state, ...extra }, new Date(now()));
    if (isFinalState(state)) unwatch(id);
  };

  /** Move the ledger from what the server's document says. */
  const fromServerDoc = (id: string, snap: DocumentSnapshot): void => {
    if (!snap.exists() || snap.metadata.hasPendingWrites) return;
    const data = snap.data() as Partial<StoredCommand>;
    const status = data.status;
    if (status !== undefined && status in FINAL_STATUS_STATE) {
      const lastError: LedgerError | undefined =
        status === 'rejected'
          ? {
              code: data.error?.code ?? 'rejected',
              message: data.error?.message ?? 'The server rejected this command.',
            }
          : undefined;
      apply(id, FINAL_STATUS_STATE[status as keyof typeof FINAL_STATUS_STATE], {
        serverStatus: status,
        ...(data.result ? { result: data.result } : {}),
        ...(lastError ? { lastError } : {}),
      });
      return;
    }
    const entry = ledger.get(id);
    // A synced command that was marked not delivered earlier clears its error.
    apply(id, 'synced', {
      ...(status ? { serverStatus: status } : {}),
      ...(entry?.lastError ? { lastError: undefined } : {}),
    });
  };

  const watch = (id: string): void => {
    if (watchers.has(id)) return;
    const unsubscribe = onSnapshot(
      doc(db, 'commands', id),
      { includeMetadataChanges: true },
      (snap) => fromServerDoc(id, snap),
      (err) => console.warn(`Watching command ${id} failed.`, err),
    );
    watchers.set(id, unsubscribe);
  };

  const write = async (command: ClientCommand): Promise<'acknowledged' | 'failed'> => {
    watch(command.id);
    try {
      await setDoc(doc(db, 'commands', command.id), command);
      apply(command.id, 'synced');
      return 'acknowledged';
    } catch (err) {
      const entry = ledger.get(command.id);
      if (entry && (entry.state === 'pending' || entry.state === 'not_delivered')) {
        apply(command.id, 'rejected', {
          lastError: {
            code: errorCode(err),
            message: `The write was refused: ${errorMessage(err)}`,
          },
        });
      }
      return 'failed';
    }
  };

  const send = (type: string, payload: unknown, options: SendOptions = {}): SendResult => {
    const t = now();
    const eventMs =
      options.occurredAtMs !== undefined && options.occurredAtMs <= t ? options.occurredAtMs : t;
    const command = buildCommand({
      type,
      payload,
      actorUid: auth.currentUser?.uid,
      deviceId,
      branchId: getBranchId(),
      appVersion,
      clockOffsetMs: typeof clockOffset === 'function' ? clockOffset() : clockOffset,
      nowMs: eventMs,
    });
    const iso = new Date(t).toISOString();
    const accepted = ledger.put({
      id: command.id,
      type: command.type,
      payload: command.payload,
      createdAt: iso,
      attemptedAt: iso,
      updatedAt: iso,
      state: 'pending',
      command,
    });
    // The ledger is written first, so a crash can never leave a queued write the ledger does not know.
    const ack = accepted.then(() => write(command));
    return { id: command.id, command, accepted, ack };
  };

  const resend = async (id: string): Promise<void> => {
    const entry = ledger.get(id);
    if (!entry || isFinalState(entry.state)) return;
    const attemptedAt = new Date(now()).toISOString();
    await ledger.patch(
      id,
      { state: 'pending', attemptedAt, lastError: undefined },
      new Date(now()),
    );
    try {
      const snap = await getDocFromServer(doc(db, 'commands', id));
      if (snap.exists()) {
        watch(id);
        fromServerDoc(id, snap);
        return;
      }
    } catch (err) {
      // Offline or unreachable: queue the write again; the SDK sends it when it can.
      console.warn(`Could not check command ${id} on the server before resending.`, errorCode(err));
    }
    await write(entry.command);
  };

  const markUndelivered = (at: number = now()): number => {
    const { online, since } = connectivity.getSnapshot();
    if (!online) return 0;
    let changed = 0;
    for (const entry of ledger.getSnapshot()) {
      if (entry.state !== 'pending') continue;
      const basis = Math.max(Date.parse(entry.attemptedAt), since);
      if (at - basis < undeliveredAfterMs) continue;
      apply(entry.id, 'not_delivered', {
        lastError: {
          code: 'not_delivered',
          message: 'The server has not received this command yet. Check the connection or resend.',
        },
      });
      changed += 1;
    }
    return changed;
  };

  return {
    ledger,
    subscribe: ledger.subscribe,
    getSnapshot: ledger.getSnapshot,
    send: send as CommandWriter['send'],
    resend,
    dismiss: (id) => ledger.patch(id, { dismissed: true }, new Date(now())),
    markUndelivered,
    async start() {
      await ledger.ready;
      for (const entry of ledger.getSnapshot()) if (!isFinalState(entry.state)) watch(entry.id);
      sweepTimer ??= setInterval(() => markUndelivered(), sweepIntervalMs);
    },
    dispose() {
      if (sweepTimer !== undefined) clearInterval(sweepTimer);
      sweepTimer = undefined;
      for (const id of [...watchers.keys()]) unwatch(id);
    },
  };
}
